import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAssessment } from '../context/AssessmentContext';
import { assessmentAreas } from '../data/assessmentData';
import { supabase } from '../supabaseClient';
import { generateReportBlob } from '../utils/exportUtils';
import ReportSummary from './ReportSummary';
import ScoreCard from './ScoreCard';
import CallToAction from './CallToAction';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiLoader, FiUser, FiBriefcase } = FiIcons;

function ReportPage() {
  const navigate = useNavigate();
  const params = useParams();
  const { state: contextState } = useAssessment();
  const [assessment, setAssessment] = useState(null);
  const [orgName, setOrgName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);

    const loadData = async () => {
      try {
        let dataToProcess = null;
        let existingPdfUrl = null;

        // Priority 1: URL Parameters (Shared View)
        if (params.orgCode && params.userEmail) {
          const decodedEmail = decodeURIComponent(params.userEmail);
          const { data, error } = await supabase
            .from('assessments')
            .select('*')
            .eq('org_code', params.orgCode)
            .eq('user_email', decodedEmail)
            .single();

          if (error) throw new Error('Report not found in database.');

          dataToProcess = {
            userInfo: {
              name: data.user_name,
              email: data.user_email,
              orgCode: data.org_code,
              ...data.about_you,
            },
            responses: data.responses,
            aboutYou: data.about_you || {},
            isComplete: true
          };
          if (data.pdf_url) existingPdfUrl = data.pdf_url;
        } 
        // Priority 2: Context State (Just finished assessment)
        else if (contextState.isComplete || Object.keys(contextState.responses).length > 0) {
          dataToProcess = contextState;
          
          // Try to fetch existing PDF if they just finished
          if (contextState.userInfo.orgCode && contextState.userInfo.email) {
            const { data } = await supabase
              .from('assessments')
              .select('pdf_url')
              .eq('org_code', contextState.userInfo.orgCode)
              .eq('user_email', contextState.userInfo.email)
              .single();
            if (data?.pdf_url) existingPdfUrl = data.pdf_url;
          }
        } 
        else {
          // No data found anywhere
          navigate('/');
          return;
        }

        setAssessment(dataToProcess);
        setPdfUrl(existingPdfUrl);

        // Fetch Org Name
        const { data: orgData } = await supabase
          .from('organizations')
          .select('name')
          .eq('code', dataToProcess.userInfo.orgCode)
          .single();
        
        setOrgName(orgData?.name || dataToProcess.aboutYou?.companyName || 'Your Organization');

      } catch (err) {
        console.error("Report load error:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [params, contextState, navigate]);

  const reportData = useMemo(() => {
    if (!assessment || !assessment.responses) return null;

    try {
      const calculatedScores = assessmentAreas.map(area => {
        let score = 0;
        area.questions.forEach((_, idx) => {
          const val = assessment.responses[`${area.id}_${idx}`];
          score += (parseInt(val) || 0);
        });

        const maxAreaScore = 35;
        const percentage = (score / maxAreaScore) * 100;

        let performance;
        if (percentage >= 90) performance = { level: 'Exceptional', color: 'text-green-700', bgColor: 'bg-green-100' };
        else if (percentage >= 80) performance = { level: 'Strong', color: 'text-emerald-700', bgColor: 'bg-emerald-100' };
        else if (percentage >= 70) performance = { level: 'Solid', color: 'text-teal-700', bgColor: 'bg-teal-100' };
        else if (percentage >= 55) performance = { level: 'Developing', color: 'text-amber-700', bgColor: 'bg-amber-100' };
        else performance = { level: 'Opportunity', color: 'text-red-700', bgColor: 'bg-red-100' };

        return { ...area, score, performance };
      });

      const totalScore = calculatedScores.reduce((sum, a) => sum + a.score, 0);
      const totalMax = 280;
      const overallPercentage = (totalScore / totalMax) * 100;

      let overallPerformance;
      if (overallPercentage >= 85) overallPerformance = { level: 'Exceptional', color: 'text-green-700', bgColor: 'bg-green-100' };
      else if (overallPercentage >= 70) overallPerformance = { level: 'Strong', color: 'text-emerald-700', bgColor: 'bg-emerald-100' };
      else if (overallPercentage >= 55) overallPerformance = { level: 'Developing', color: 'text-amber-700', bgColor: 'bg-amber-100' };
      else overallPerformance = { level: 'Opportunity', color: 'text-red-700', bgColor: 'bg-red-100' };

      return { areaScores: calculatedScores, overallScore: totalScore, overallPerformance };
    } catch (err) {
      console.error("Scoring error:", err);
      return null;
    }
  }, [assessment]);

  const handleGenerateAndSavePdf = async () => {
    if (!assessment || !reportData) return;
    setIsGeneratingPdf(true);
    try {
      const pdfBlob = generateReportBlob({
        userInfo: assessment.userInfo,
        responses: assessment.responses,
        aboutYou: assessment.aboutYou,
        score: reportData.overallScore
      });

      const fileName = `${assessment.userInfo.orgCode}_${assessment.userInfo.email}_${Date.now()}.pdf`.replace(/[^a-zA-Z0-9._-]/g, '');
      const filePath = `public/${fileName}`;

      const { error: uploadError } = await supabase.storage.from('reports').upload(filePath, pdfBlob);
      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage.from('reports').getPublicUrl(filePath);

      await supabase.from('assessments')
        .update({ pdf_url: publicUrl })
        .eq('org_code', assessment.userInfo.orgCode)
        .eq('user_email', assessment.userInfo.email);

      setPdfUrl(publicUrl);
    } catch (err) {
      console.error("PDF Error:", err);
      alert("Failed to save PDF link.");
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><FiLoader className="w-10 h-10 animate-spin text-blue-600" /></div>;
  if (error) return <div className="p-8 text-center text-red-600">Error: {error}</div>;
  if (!assessment || !reportData) return null;

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        <header className="bg-white rounded-2xl shadow-xl p-6 mb-8 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">Organizational Health Report</h1>
          <div className="flex flex-col md:flex-row justify-center items-center gap-2 md:gap-6 text-gray-600 mt-4">
            <span className="flex items-center gap-2 font-medium"><SafeIcon icon={FiUser} /> {assessment.userInfo.name}</span>
            <span className="flex items-center gap-2 font-medium"><SafeIcon icon={FiBriefcase} /> {orgName}</span>
          </div>
        </header>

        <ReportSummary 
          overallScore={reportData.overallScore} 
          overallPerformance={reportData.overallPerformance} 
          areaScores={reportData.areaScores} 
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
          {reportData.areaScores.map((area, idx) => (
            <ScoreCard key={area.id} area={area} responses={assessment.responses} delay={0.3 + idx * 0.1} />
          ))}
        </div>

        <CallToAction 
          userInfo={assessment.userInfo} 
          overallScore={reportData.overallScore} 
          pdfUrl={pdfUrl} 
          onGeneratePdf={handleGenerateAndSavePdf} 
          isGeneratingPdf={isGeneratingPdf} 
        />
      </div>
    </div>
  );
}

export default ReportPage;